import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { convertAmountService } from 'src/services';
import { useWebSocketStore } from 'src/store/main';
import { isEmptyString, isUndefinedOrNull } from 'src/utils/general';

export const useAmountFormatStore = defineStore('amount_format', {
  state: () => ({
    convertion_amount_cache: {} as dynamicObj,
    websocket_store: useWebSocketStore(),
  }),
  getters: {},
  actions: {
    getRequestValues(values: any, dest_locale: string) {
      const cached_values = [] as dynamicObj[];
      for (const [index, item] of values.entries()) {
        cached_values.push({
          key: item.key,
          value: this.convertion_amount_cache[item.key + '_' + dest_locale],
        });
      }

      return cached_values;
    },

    // TODO:: check values too from cache
    async formatMultiValues(
      values: any,
      source_locale = '',
      dest_locale = 'en_US',
    ) {
      try {
        let response = {} as dynamicObj | undefined;
        const notCachedValues = values.filter((item: dynamicObj) => {
          return !this.hasConvertedAmountInCache(item.key, dest_locale);
        });

        // make call to server
        if (notCachedValues.length > 0) {
          if (this.websocket_store.isConnected) {
            response = await this.sendWebSocketMessage({
              values: notCachedValues,
              source_locale,
              dest_locale,
            });
          } else {
            response = await convertAmountService.convertMultiValuesAsLocale(
              notCachedValues,
              source_locale,
              dest_locale,
            );
          }

          if (!response || !response.success) {
            throw response;
          }

          this.addValuesToCache(response.data.values, dest_locale);
        }

        const result = this.getRequestValues(values, dest_locale);
        return result;
      } catch (err) {
        logError(err);
        return [] as dynamicObj[];
      }
    },

    async sendWebSocketMessage(data: dynamicObj) {
      const response = await this.websocket_store.send('calculation', data);
      return response;
    },

    addValuesToCache(values: any, target_locale: string) {
      Array.from(values).forEach((row: any, index: any) => {
        let dest_locale = target_locale;
        if (row.hasOwnProperty('dest_locale')) {
          dest_locale = row.dest_locale;
        }
        this.convertion_amount_cache[row.key + '_' + dest_locale] = row.value;
      });
    },

    hasConvertedAmountInCache(value: any, target_locale: string) {
      if (
        this.convertion_amount_cache.hasOwnProperty(value + '_' + target_locale)
      ) {
        return true;
      }
      return false;
    },

    async formatValue(
      value: any,
      source_locale: string,
      dest_locale = 'en_US',
    ) {
      try {
        if (this.hasConvertedAmountInCache(value, dest_locale)) {
          return this.convertion_amount_cache[
            value + '_' + dest_locale
          ] as string;
        }

        if (isUndefinedOrNull(value) || isEmptyString(value)) {
          value = '0';
        }

        let response = '';
        if (this.websocket_store.isConnected) {
          const values = [];
          values.push({
            key: value,
            value: value,
          });
          const result = await this.sendWebSocketMessage({
            values,
            source_locale,
            dest_locale,
          });
          if (!result || !result.success) {
            throw result;
          }

          response = result.data.values[0].value;
        } else {
          response = await convertAmountService.convertValueAccordingToLocale(
            value,
            source_locale,
            dest_locale,
          );
        }

        this.convertion_amount_cache[value + '_' + dest_locale] = response;
        return response;
      } catch (err) {
        logError(err);
        return '0.00';
      }
    },

    resetStore() {
      this.convertion_amount_cache = {} as dynamicObj;
    },

    searchValueByKey(key: string, arrayOfObjects: dynamicObj[]) {
      const foundObject = arrayOfObjects.find((obj) => obj.key === key);
      if (!foundObject) {
        return '';
      }

      return foundObject.value as string;
    },
  },
});
